import type { AeEvaluatedSection, AeTemplateLogicRule } from '@/types/assessmentEngine';
import {
  flattenPlacedQuestions,
  getNextQuestionKeyWithTriggers,
  nextPresentationStepIndexWithTriggers,
  type NavigableQuestion,
} from './logicRuleNavigation';

export type { NavigableQuestion };

/** Walk evaluated tree; collect visible questions in document order. */
export function flattenEvaluatedQuestions(sections: AeEvaluatedSection[]): NavigableQuestion[] {
  return flattenPlacedQuestions(sections).map((q) => ({
    id: q.id,
    question_key: q.question_key,
    section_id: q.section_id,
  }));
}

/** Next question after currentKey respecting skip-to-section rules and trigger scope. */
export function getNextQuestionKey(
  currentKey: string,
  sections: AeEvaluatedSection[],
  rules: AeTemplateLogicRule[],
  answers: Record<string, string | number | boolean | null>
): string | null {
  return getNextQuestionKeyWithTriggers(currentKey, sections, rules, answers);
}

/** Build linear question order from start using current answers and skip rules. */
export function buildNavigableQuestionKeys(
  sections: AeEvaluatedSection[],
  rules: AeTemplateLogicRule[],
  answers: Record<string, string | number | boolean | null>
): string[] {
  const flat = flattenEvaluatedQuestions(sections);
  if (flat.length === 0) return [];

  const keys: string[] = [];
  const visited = new Set<string>();
  let current: string | null = flat[0].question_key;

  while (current && !visited.has(current)) {
    visited.add(current);
    keys.push(current);
    current = getNextQuestionKey(current, sections, rules, answers);
  }

  return keys;
}

/** Reorder flat visible questions using skip-aware navigation path (excludes branched-off questions). */
export function orderQuestionsByNavigation<T extends { question_key: string }>(
  flat: T[],
  sections: AeEvaluatedSection[],
  rules: AeTemplateLogicRule[],
  answers: Record<string, string | number | boolean | null>
): T[] {
  const order = buildNavigableQuestionKeys(sections, rules, answers);
  const byKey = new Map(flat.map((q) => [q.question_key, q]));
  return order.map((k) => byKey.get(k)).filter((q): q is T => !!q);
}

/** After completing a presentation step, honor skip rules on step questions or advance linearly. */
export function nextPresentationStepIndex(
  completedStep: { id: string; questions: Array<{ id: string; question_key: string }> },
  currentStepIndex: number,
  steps: Array<{ questions: Array<{ question_key: string }> }>,
  sections: AeEvaluatedSection[],
  rules: AeTemplateLogicRule[],
  answers: Record<string, string | number | boolean | null>,
  presentationStyle: 'section' | 'subsection' = 'section'
): number {
  return nextPresentationStepIndexWithTriggers(
    completedStep,
    currentStepIndex,
    steps,
    sections,
    rules,
    answers,
    presentationStyle
  );
}
